import { useEffect, useState } from "react";
import { Button, Group, Modal, Paper, Text, ThemeIcon } from "@mantine/core";
import { IconCircleCheck, IconSparkles } from "@tabler/icons-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUserContext } from "@/context/UserContext";
import {
  isWebsiteProfileJobInProgress,
  shouldDeferCompanyProfile,
} from "@/shared/constants/clientOnboarding.const";

let openPrompt = () => {};

/** Opens the complete-profile popup from anywhere under AppLayout. */
export function openCompleteProfilePrompt() {
  openPrompt();
}

function profileReviewModalStorageKey(companyId: string) {
  return `gt:profile-review-modal:${companyId}`;
}

export function CompleteProfilePrompt() {
  const [opened, setOpened] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { userDetails } = useUserContext();
  const companyId = userDetails?.company?.id;
  const jobStatus = (
    userDetails?.company as { websiteProfileJob?: { status?: string | null } | null } | null | undefined
  )?.websiteProfileJob?.status;
  const generating = isWebsiteProfileJobInProgress(
    userDetails?.company as { websiteProfileJob?: { status?: string | null } | null } | null | undefined,
  );
  const readyForReview = jobStatus === "READY_FOR_REVIEW";
  const generationFailed = jobStatus === "FAILED";

  useEffect(() => {
    openPrompt = () => setOpened(true);
    return () => {
      openPrompt = () => {};
    };
  }, []);

  // Auto-open once when background generation becomes ready for review (banner stays).
  useEffect(() => {
    if (!readyForReview || !companyId) return;
    if (location.pathname === "/profile/edit" || location.pathname === "/create-profile") return;
    try {
      const key = profileReviewModalStorageKey(companyId);
      if (sessionStorage.getItem(key) === "1") return;
      sessionStorage.setItem(key, "1");
      setOpened(true);
    } catch {
      setOpened(true);
    }
  }, [readyForReview, companyId, location.pathname]);

  return (
    <Modal
      opened={opened}
      onClose={() => setOpened(false)}
      title={
        generating ? (
          <Group gap="sm" wrap="nowrap">
            <ThemeIcon size={36} radius="xl" variant="light" color="blue">
              <IconSparkles size={18} />
            </ThemeIcon>
            <Text fw={600} fz="md" c="#0B3A6A" tt="none">
              Your profile is being created
            </Text>
          </Group>
        ) : readyForReview ? (
          <Group gap="sm" wrap="nowrap">
            <ThemeIcon size={36} radius="xl" variant="light" color="teal">
              <IconCircleCheck size={18} />
            </ThemeIcon>
            <Text fw={600} fz="md" c="#065F46" tt="none">
              Review your profile
            </Text>
          </Group>
        ) : generationFailed ? (
          "Create your company profile"
        ) : (
          "Complete your company profile"
        )
      }
      centered
      radius="md"
      styles={
        generating
          ? {
              header: {
                background: "linear-gradient(180deg, #E8F1FC 0%, #ffffff 100%)",
                paddingBottom: 12,
              },
            }
          : readyForReview
            ? {
                header: {
                  background: "linear-gradient(180deg, #ECFDF5 0%, #ffffff 100%)",
                  paddingBottom: 12,
                },
              }
            : undefined
      }
    >
      {generating ? (
        <>
          <Paper
            p="md"
            radius="md"
            style={{
              backgroundColor: "#F3F8FD",
              border: "1px solid #D3E4F5",
            }}
          >
            <Text size="sm" c="#1E3A5F" lh={1.6}>
              <Text span fw={700} c="#0B3A6A">
                GTsetu AI
              </Text>{" "}
              is building your company profile in the background. You can join, shortlist, and reach out once it is ready.
            </Text>
          </Paper>
          <Text size="sm" c="#5B7C99" mt="sm">
            Explore other companies while you wait.
          </Text>
          <Group justify="flex-end" mt="lg">
            <Button onClick={() => setOpened(false)} styles={{ root: { backgroundColor: "#04305c" } }}>
              Keep exploring
            </Button>
          </Group>
        </>
      ) : readyForReview ? (
        <>
          <Paper
            p="md"
            radius="md"
            style={{
              backgroundColor: "#F0FDF4",
              border: "1px solid #BBF7D0",
            }}
          >
            <Text size="sm" c="#14532D" lh={1.6}>
              Your company profile is ready. Review it and fill in any remaining details to access collaboration.
            </Text>
          </Paper>
          <Group justify="flex-end" mt="lg">
            <Button variant="default" onClick={() => setOpened(false)}>
              Not now
            </Button>
            <Button
              color="teal"
              onClick={() => {
                setOpened(false);
                navigate("/profile/edit");
              }}
            >
              Review profile
            </Button>
          </Group>
        </>
      ) : (
        <>
          <Text size="sm">You can look through expos, clusters, and experts.</Text>
          <Text size="sm">
            {generationFailed
              ? "We could not create your profile from your website. Create it yourself before you join, shortlist, or reach out."
              : "Create your company profile before you join, shortlist, or reach out."}
          </Text>
          <Group justify="flex-end" mt="lg">
            <Button variant="default" onClick={() => setOpened(false)}>
              Not now
            </Button>
            <Button
              onClick={() => {
                setOpened(false);
                navigate("/create-profile");
              }}
            >
              {generationFailed ? "Create profile" : "Complete profile"}
            </Button>
          </Group>
        </>
      )}
    </Modal>
  );
}

/** True when this client may browse but must finish the company form before an action. */
export function useDeferredProfileAction() {
  const { userDetails } = useUserContext();
  const profileRequired = shouldDeferCompanyProfile(userDetails);
  const blockAction = () => {
    if (!profileRequired) return false;
    openCompleteProfilePrompt();
    return true;
  };
  return { profileRequired, blockAction };
}
